// react
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

// radix
import * as RadixSelect from '@radix-ui/react-select';

// components
import { BaseField, FieldProps } from '@/components/atoms/BaseField/BaseField';
import { Icon, Icons } from '@/components/atoms/Icon';

// theme
import { Color } from '@/theme/color';

/**
 * Select allows the user to select a value from a list of options.
 */
export const Select = ({
  className,
  id,
  label,
  description,
  tooltip,
  value,
  onChange,
  options,
  placeholder = 'Select',
  ariaLabel,
  isDisabled = false,
  isRequired = false,
  isHideTitle = false,
}: SelectProps) => {
  // state
  const [isOpen, setIsOpen] = useState(false);

  // refs
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = contentRef.current;
    if (!node) return;
    node.addEventListener('touchend', (e) => e.preventDefault());
    return () => {
      node.removeEventListener('touchend', (e) => e.preventDefault());
    };
  }, [contentRef.current, open]);

  return (
    <BaseField
      id={id}
      label={label}
      description={description}
      tooltip={tooltip}
      isRequired={isRequired}
      isHideTitle={isHideTitle}
      className={className}
    >
      <RadixSelect.Root
        open={isOpen}
        onOpenChange={setIsOpen}
        disabled={isDisabled}
        value={value}
        onValueChange={(val) => val && onChange?.(val)}
      >
        <StyledSelectTrigger
          aria-label={ariaLabel}
          onPointerDown={(e) => e.preventDefault()}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <StyledSelectValue>
            <RadixSelect.Value placeholder={placeholder} />
          </StyledSelectValue>
          <StyledSelectIcon>
            <Icon icon={isOpen ? 'chevron-up' : 'chevron-down'} fontSize={16} />
          </StyledSelectIcon>
        </StyledSelectTrigger>

        <RadixSelect.Portal>
          <StyledSelectContent position='popper' ref={contentRef}>
            <Scroll>
              <RadixSelect.Viewport>
                {options
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((option) => (
                    <StyledSelectItem key={option.value} value={option.value}>
                      <StyledSelectItemText>
                        {option.label}
                      </StyledSelectItemText>
                      <StyledSelectItemIndicator>
                        <Icon icon='check' fontSize={16} />
                      </StyledSelectItemIndicator>
                    </StyledSelectItem>
                  ))}
              </RadixSelect.Viewport>
            </Scroll>
          </StyledSelectContent>
        </RadixSelect.Portal>
      </RadixSelect.Root>
    </BaseField>
  );
};

const StyledSelectTrigger = styled(RadixSelect.Trigger)`
  // default styles
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${(props) => props.theme.spacing[8]};
  min-width: 100px;

  border-radius: 8px;
  width: 100%;
  font: ${(props) => props.theme.typography.body.md.regular};
  background: ${(props) => props.theme.color.neutral[10]};
  color: ${(props) => props.theme.color.neutral[90]};
  border: 1px solid ${(props) => props.theme.color.neutral[60]};
  padding: ${(props) => props.theme.spacing[12]};
  cursor: pointer;

  min-height: 48px;
  max-height: 48px;

  &:hover {
    background: ${(props) => props.theme.color.neutral[20]};
  }

  &:focus {
    outline: 3px solid ${(props) => props.theme.color.primary[20]};
  }

  &:disabled {
    background: ${(props) => props.theme.color.neutral[30]};
    color: ${(props) => props.theme.color.neutral[50]};
    pointer-events: none;
  }
`;

const StyledSelectValue = styled.div`
  // default styles
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const StyledSelectIcon = styled(RadixSelect.Icon)`
  // default styles
  display: flex;
  align-items: center;
`;

const StyledSelectContent = styled(RadixSelect.Content)`
  width: var(--radix-select-trigger-width);

  z-index: 5000001;
  background: ${(props) => props.theme.color.neutral[10]};
  box-shadow: ${(props) => props.theme.elevation[3]};
  border-radius: 8px;
  overflow: hidden;
  margin-top: ${(props) => props.theme.spacing[4]};
`;

const Scroll = styled.div`
  padding: ${(props) => props.theme.spacing[16]} 0;
  max-height: var(--radix-select-content-available-height);
  overflow: auto;
`;

const StyledSelectItem = styled(RadixSelect.Item)`
  // default styles
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${(props) => props.theme.spacing[8]};

  font: ${(props) => props.theme.typography.body.md.regular};
  padding: ${(props) => props.theme.spacing[12]};
  color: ${(props) => props.theme.color.neutral[90]};
  user-select: none;
  cursor: pointer;

  &:focus {
    outline: none;
    background: ${(props) => props.theme.color.neutral[30]};
  }
`;

const StyledSelectItemText = styled(RadixSelect.ItemText)`
  // default
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const StyledSelectItemIndicator = styled(RadixSelect.ItemIndicator)`
  // default styles
  display: flex;
  align-items: center;
`;

export interface Option {
  value: string;
  label: string;
  order?: number;
  icon?: Icons;
  iconFill?: Color;
}

export interface SelectProps extends FieldProps<string> {
  className?: string;
  options: Option[];
}

Select.componentName = 'Select';

export default Select;
